import React, { useEffect, useState } from "react";
import {
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col
} from "reactstrap";
import "../../global.css";
import AWSS3Uploader from "../../components/Common/S3uploader";
import {
  AddDeliveryBoy,
  DeleteDeliveryBoy,
  UpdateDeliveryBoy,
  GetDeliveryBoys,
  GetStores,
  DisableRider,
  EnableRider,
  ResetRiderPassword,
} from "../../requests";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import LoadingSpinner from "../../components/LoadingSpinner";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import classes from "./styles.module.css";

const Delivery = () => {
  document.title = "Delivery Riders | Susipizza";

  const [name, setName] = useState("");
  const [photo, setPhoto] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [idProofPhoto, setIdProofPhoto] = useState("");
  const [selectedStore, setSelectedStore] = useState([]); // store names array
  const [selectedStoreId, setSelectedStoreId] = useState(""); // decoupled store_id
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  
  // New fields
  const [email, setEmail] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [vehicleType, setVehicleType] = useState("Bike");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [emergencyContact, setEmergencyContact] = useState("");
  const [joiningDate, setJoiningDate] = useState("");
  const [status, setStatus] = useState("Offline");

  const [isModalVisible, setisModalVisible] = useState(false);
  const [ButtonText, setButtonText] = useState("");
  const [StoresData, setStoresData] = useState([]);
  const [DeliveryData, setDeliveryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [targetId, setTargetId] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  // Password reset state
  const [isPasswordResetModalVisible, setIsPasswordResetModalVisible] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  const userRole = useSelector((state) => state?.login?.role);
  const usernameAd = useSelector((state) => state?.login?.user);

  const toggle = () => {
    setisModalVisible(!isModalVisible);
    if (!isModalVisible) {
      // Clear fields on open
      setName("");
      setPhoto("");
      setPhoneNumber("");
      setAddress("");
      setIdProofPhoto("");
      setSelectedStore([]);
      setSelectedStoreId("");
      setUsername("");
      setPassword("");
      setEmail("");
      setVehicleNumber("");
      setVehicleType("Bike");
      setLicenseNumber("");
      setEmergencyContact("");
      setJoiningDate("");
      setStatus("Offline");
      setButtonText("Create Rider");
      // The reset above blanks selectedStore/selectedStoreId, which for a
      // store-tied admin should immediately be re-locked to their own store
      // (checkAdmin already no-ops for superadmin) — without this, the
      // "Assigned Store" field opened blank every time "Add Rider" was
      // clicked, even though the admin can only ever create riders for
      // their own store.
      checkAdmin();
    }
  };

  const togglePasswordResetModal = (rider = null) => {
    if (rider) {
      setSelectedDeliveryId(rider.id);
    }
    setIsPasswordResetModalVisible(!isPasswordResetModalVisible);
    setNewPassword("");
  };

  const handleDeleteClick = (id) => {
    setTargetId(id);
    setConfirmModal(true);
  };

  const confirmDelete = async () => {
    setConfirmModal(false);
    if (!targetId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      await DeleteDeliveryBoy(token, targetId);
      DeliveryBoysData();
      toast.success("Delivery Boy Deleted successfully");
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
      setTargetId(null);
    }
  };

  const handleSave = async () => {
    if (!name || !phoneNumber || !username) {
      toast.error("Please fill in Name, Phone Number and Username");
      return;
    }
    if (ButtonText !== "Update" && (!password || password.length < 6)) {
      toast.error("Password is required and must be at least 6 characters");
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const payload = {
        name,
        photo,
        idProof: idProofPhoto,
        phoneNumber,
        address,
        store: selectedStore,
        store_id: selectedStoreId || null,
        username,
        email,
        vehicle_number: vehicleNumber,
        vehicle_type: vehicleType,
        license_number: licenseNumber,
        emergency_contact: emergencyContact,
        joining_date: joiningDate,
        status,
        is_active: status !== "Disabled",
      };

      if (ButtonText === "Update") {
        await UpdateDeliveryBoy(token, selectedDeliveryId, payload);
        toast.success("Delivery Rider Updated successfully");
      } else {
        await AddDeliveryBoy(token, { ...payload, password });
        toast.success("Delivery Rider Created successfully");
      }

      toggle();
      DeliveryBoysData();
    } catch (err) {
      console.error("Save delivery boy error:", err.response?.data || err);
      const validationErrs = err.response?.data?.validationErrors;
      if (validationErrs && Array.isArray(validationErrs)) {
        validationErrs.forEach(e => toast.error(`${e.field}: ${e.message}`));
      } else {
        toast.error(err.response?.data?.error || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rowData) => {
    setButtonText("Update");
    setSelectedDeliveryId(rowData.id);
    
    setName(rowData.name || "");
    setPhoto(rowData.photo || "");
    setPhoneNumber(rowData.phoneNumber || "");
    setAddress(rowData.address || "");
    setIdProofPhoto(rowData.idProof || "");
    setSelectedStore(rowData.store || []);
    setSelectedStoreId(rowData.store_id || "");
    setUsername(rowData.username || "");
    setEmail(rowData.email || "");
    setVehicleNumber(rowData.vehicle_number || "");
    setVehicleType(rowData.vehicle_type || "Bike");
    setLicenseNumber(rowData.license_number || "");
    setEmergencyContact(rowData.emergency_contact || "");
    setJoiningDate(rowData.joining_date || "");
    setStatus(rowData.status || "Offline");

    setisModalVisible(true);
  };

  const handleToggleStatus = async (rider) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (rider.status === "Disabled" || !rider.is_active) {
        await EnableRider(token, rider.id);
        toast.success(`${rider.name} enabled successfully`);
      } else {
        await DisableRider(token, rider.id);
        toast.success(`${rider.name} disabled successfully`);
      }
      DeliveryBoysData();
    } catch (err) {
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordSave = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      await ResetRiderPassword(token, selectedDeliveryId, newPassword);
      toast.success("Password reset successfully");
      togglePasswordResetModal();
    } catch (err) {
      toast.error("Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const dbGetStores = async () => {
    try {
      setLoading(true);
      const resp = await GetStores();
      setStoresData(resp.data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const DeliveryBoysData = async () => {
    try {
      setLoading(true);
      // GetDeliveryBoys is now token-scoped server-side — a store-tied
      // admin automatically gets only their own store's riders, superadmin
      // gets all of them. No more branching (the old store-admin branch
      // called a broken username-matching endpoint — see requests.js).
      const token = localStorage.getItem("token");
      const resp = await GetDeliveryBoys(token);
      setDeliveryData(resp.data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dbGetStores();
    DeliveryBoysData();
  }, []);

  // Was matching stores by a `username` field current store creation
  // doesn't set (same broken pattern as GetOrdersByUserName — see
  // requests.js) — storeId from the JWT/login response is real and
  // reliable, stored in localStorage right alongside the token.
  const checkAdmin = () => {
    if (userRole === "admin") {
      const myStoreId = localStorage.getItem("storeId");
      const storeObj = StoresData.find((storex) => (storex.storeId || storex.id) === myStoreId);
      if (storeObj) {
        setSelectedStore([storeObj.name]);
        setSelectedStoreId(storeObj.storeId || storeObj.id);
      }
    }
  };

  useEffect(() => {
    checkAdmin();
  }, [userRole, usernameAd, StoresData]);

  // Client-side search and filtering
  const filteredRiders = DeliveryData.filter((rider) => {
    const term = searchQuery.toLowerCase();
    const storeText = Array.isArray(rider.store) ? rider.store.join(", ") : rider.store || "";
    return (
      (rider.name || "").toLowerCase().includes(term) ||
      (rider.phoneNumber || "").toLowerCase().includes(term) ||
      storeText.toLowerCase().includes(term) ||
      (rider.vehicle_type || "").toLowerCase().includes(term) ||
      (rider.status || "").toLowerCase().includes(term)
    );
  });

  const getInitials = (n) => {
    if (!n) return "R";
    const parts = n.split(" ");
    return parts.map(p => p.charAt(0)).join("").toUpperCase().slice(0, 2);
  };

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading} />
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Delivery Management" breadcrumbItem="Delivery Riders" />

          {/* Search and Action Header */}
          <div className={classes.pageHeader}>
            <div className={classes.titleSection}>
              <h2>Riders Directory</h2>
              <p>Create, update, enable, and monitor delivery riders details.</p>
            </div>
            <div className={classes.searchBarContainer}>
              <div className={classes.searchField}>
                <span className={`${classes.searchIcon} mdi mdi-magnify`}></span>
                <input
                  type="text"
                  placeholder="Search riders by name, store, phone..."
                  className={classes.searchInput}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <button
                type="button"
                className={classes.addRiderBtn}
                onClick={toggle}
              >
                <span className="mdi mdi-plus"></span>
                Add Rider
              </button>
            </div>
          </div>

          {/* Cards Grid */}
          {filteredRiders.length > 0 ? (
            <div className={classes.ridersGrid}>
              {filteredRiders.map((rider) => {
                const riderStatus = rider.status || "Offline";
                const isRiderDisabled = rider.status === "Disabled" || !rider.is_active;
                
                return (
                  <div key={rider.id} className={classes.riderCard}>
                    <div className={classes.riderHeader}>
                      <div className={classes.avatarWrapper}>
                        {rider.photo ? (
                          <img
                            src={rider.photo}
                            alt={rider.name}
                            className={classes.riderAvatar}
                          />
                        ) : (
                          <div className={classes.avatarInitials}>
                            {getInitials(rider.name)}
                          </div>
                        )}
                        <span
                          className={`${classes.statusIndicator} ${
                            riderStatus === "Available"
                              ? classes.statusAvailable
                              : riderStatus === "Assigned" || riderStatus === "Delivering"
                              ? classes.statusAssigned
                              : classes.statusOffline
                          }`}
                        />
                      </div>
                      <div className={classes.riderBasicInfo}>
                        <h4 className={classes.riderName}>{rider.name}</h4>
                        <p className={classes.riderUsername}>@{rider.username}</p>
                      </div>
                      <span
                        className={`${classes.statusBadge} ${
                          riderStatus === "Available"
                            ? classes.badgeAvailable
                            : riderStatus === "Assigned"
                            ? classes.badgeAssigned
                            : riderStatus === "Delivering"
                            ? classes.badgeDelivering
                            : riderStatus === "Disabled"
                            ? classes.badgeDisabled
                            : classes.badgeOffline
                        }`}
                      >
                        {riderStatus}
                      </span>
                    </div>

                    <div className={classes.riderDetails}>
                      <div className={classes.detailRow}>
                        <span className={classes.detailLabel}>Phone:</span>
                        <span className={classes.detailValue}>{rider.phoneNumber}</span>
                      </div>
                      {rider.email && (
                        <div className={classes.detailRow}>
                          <span className={classes.detailLabel}>Email:</span>
                          <span className={classes.detailValue}>{rider.email}</span>
                        </div>
                      )}
                      <div className={classes.detailRow}>
                        <span className={classes.detailLabel}>Vehicle:</span>
                        <span className={classes.detailValue}>
                          {rider.vehicle_type || "N/A"} {rider.vehicle_number ? `(${rider.vehicle_number})` : ""}
                        </span>
                      </div>
                      <div className={classes.detailRow}>
                        <span className={classes.detailLabel}>Store:</span>
                        <span className={classes.detailValue}>
                          {Array.isArray(rider.store) ? rider.store.join(", ") : rider.store || "N/A"}
                        </span>
                      </div>
                      {rider.license_number && (
                        <div className={classes.detailRow}>
                          <span className={classes.detailLabel}>License:</span>
                          <span className={classes.detailValue}>{rider.license_number}</span>
                        </div>
                      )}
                    </div>

                    <div className={classes.riderActions}>
                      <button
                        className={`${classes.actionBtn} ${classes.btnEdit}`}
                        onClick={() => handleEdit(rider)}
                      >
                        <span className="mdi mdi-pencil"></span>
                        Edit
                      </button>
                      <button
                        className={classes.actionBtn}
                        onClick={() => togglePasswordResetModal(rider)}
                      >
                        <span className="mdi mdi-key-variant"></span>
                        Reset
                      </button>
                      <button
                        className={`${classes.actionBtn} ${isRiderDisabled ? classes.btnEnable : classes.btnDisable}`}
                        onClick={() => handleToggleStatus(rider)}
                      >
                        <span className={`mdi ${isRiderDisabled ? "mdi-play" : "mdi-stop"}`}></span>
                        {isRiderDisabled ? "Enable" : "Disable"}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        style={{ padding: "8px" }}
                        onClick={() => handleDeleteClick(rider.id)}
                      >
                        <span className="mdi mdi-delete"></span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={classes.noResults}>
              <span className="mdi mdi-account-alert fs-24 mb-2 d-block"></span>
              No riders found matching the search criteria.
            </div>
          )}
        </Container>
      </div>

      {/* Add / Edit Modal */}
      <Modal isOpen={isModalVisible} toggle={toggle} centered size="lg">
        <ModalHeader toggle={toggle}>
          {ButtonText === "Update" ? "Update Rider Profile" : "Register New Rider"}
        </ModalHeader>
        <ModalBody className="modal-color">
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="name">Full Name *</Label>
                <Input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Rahul Kumar"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="phoneNumber">Phone Number *</Label>
                <Input
                  type="text"
                  id="phoneNumber"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="e.g. 9876543210"
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="email">Email Address</Label>
                <Input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g. rider@susipizza.com"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="username">Username *</Label>
                <Input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Unique login name"
                />
              </FormGroup>
            </Col>
          </Row>

          {ButtonText !== "Update" && (
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="password">Login Password *</Label>
                  <Input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    autoComplete="new-password"
                  />
                </FormGroup>
              </Col>
            </Row>
          )}

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="vehicleType">Vehicle Type</Label>
                <Input
                  type="select"
                  id="vehicleType"
                  value={vehicleType}
                  onChange={(e) => setVehicleType(e.target.value)}
                >
                  <option value="Bike">Bike</option>
                  <option value="Scooter">Scooter</option>
                  <option value="Car">Car</option>
                  <option value="Cycle">Cycle</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="vehicleNumber">Vehicle Registration Number</Label>
                <Input
                  type="text"
                  id="vehicleNumber"
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value)}
                  placeholder="e.g. JH-01-AB-1234"
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="licenseNumber">Driving License Number</Label>
                <Input
                  type="text"
                  id="licenseNumber"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  placeholder="e.g. DL-1234567890123"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="storeSelect">Assigned Store</Label>
                {userRole === "admin" ? (
                  <Input
                    type="text"
                    id="storeSelect"
                    value={selectedStore.join(", ")}
                    disabled
                  />
                ) : (
                  <Input
                    type="select"
                    id="storeSelect"
                    value={selectedStoreId}
                    onChange={(e) => {
                      const sId = e.target.value;
                      setSelectedStoreId(sId);
                      const matched = StoresData.find((s) => (s.storeId || s.id) === sId);
                      if (matched) {
                        setSelectedStore([matched.name]);
                      } else {
                        setSelectedStore([]);
                      }
                    }}
                  >
                    <option value="">Select a Store</option>
                    {StoresData.map((store) => (
                      <option key={store.id} value={store.storeId || store.id}>
                        {store.name} ({store.storeId || "N/A"})
                      </option>
                    ))}
                  </Input>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="emergencyContact">Emergency Contact</Label>
                <Input
                  type="text"
                  id="emergencyContact"
                  value={emergencyContact}
                  onChange={(e) => setEmergencyContact(e.target.value)}
                  placeholder="Name / Phone"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="joiningDate">Joining Date</Label>
                <Input
                  type="date"
                  id="joiningDate"
                  value={joiningDate}
                  onChange={(e) => setJoiningDate(e.target.value)}
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label for="address">Residential Address</Label>
            <Input
              type="textarea"
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Full address details..."
            />
          </FormGroup>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Rider Profile Photo</Label>
                <AWSS3Uploader onUploadComplete={(url) => setPhoto(url)} folder="delivery-riders" />
                {photo && (
                  <img
                    src={photo}
                    alt="Preview"
                    className="mt-2 img-thumbnail"
                    style={{ maxHeight: "80px" }}
                  />
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>ID Proof Doc Photo</Label>
                <AWSS3Uploader onUploadComplete={(url) => setIdProofPhoto(url)} folder="delivery-riders" />
                {idProofPhoto && (
                  <img
                    src={idProofPhoto}
                    alt="Preview ID"
                    className="mt-2 img-thumbnail"
                    style={{ maxHeight: "80px" }}
                  />
                )}
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label for="statusSelect">Availability Status</Label>
            <Input
              type="select"
              id="statusSelect"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="Offline">Offline</option>
              <option value="Available">Available</option>
              <option value="Assigned">Assigned</option>
              <option value="Delivering">Delivering</option>
              <option value="Disabled">Disabled</option>
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSave}>
            {ButtonText}
          </Button>
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Admin Reset Password Modal */}
      <Modal isOpen={isPasswordResetModalVisible} toggle={togglePasswordResetModal} centered>
        <ModalHeader toggle={togglePasswordResetModal}>Reset Rider Password</ModalHeader>
        <ModalBody className="modal-color">
          <FormGroup>
            <Label for="newPassword">New Password</Label>
            <Input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="Enter new password (min 6 chars)"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleResetPasswordSave}>
            Reset Password
          </Button>
          <Button color="secondary" onClick={togglePasswordResetModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(!confirmModal)} centered>
        <ModalHeader toggle={() => setConfirmModal(!confirmModal)}>Confirm Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this delivery boy? This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={confirmDelete}>Delete</Button>{' '}
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Delivery;
